/**
 * ThreeScene — Lightweight 3D visualization for GeoVera "Future"
 *
 * - Extrudes selected vector features
 * - Simple terrain mesh from points or raster (stub)
 * - Fly controls (mouse)
 *
 * Loads Three.js from CDN if not bundled (graceful for this environment).
 * In production: `npm install three @react-three/fiber @react-three/drei` + proper React integration.
 */
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

declare global {
  interface Window {
    THREE?: any;
  }
}

interface ThreeSceneProps {
  features?: any[];
  heightExaggeration?: number;
}

const ThreeScene: React.FC<ThreeSceneProps> = ({ features = [], heightExaggeration = 1.5 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<any>(null);

  useEffect(() => {
    let cleanup = () => {};

    const loadThree = async () => {
      if (!window.THREE) {
        // Load from CDN for immediate demo capability
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Three.js CDN'));
          document.head.appendChild(script);
        });
      }

      const THREE = window.THREE;
      if (!THREE || !containerRef.current) return;

      const container = containerRef.current;
      const width = container.clientWidth || 320;
      const height = 280;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x1a1f2e);

      const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 10000);
      camera.position.set(0, 120, 220);

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      container.appendChild(renderer.domElement);

      // Lights
      scene.add(new THREE.AmbientLight(0x8899aa, 0.6));
      const dir = new THREE.DirectionalLight(0xffffff, 0.9);
      dir.position.set(100, 200, 50);
      scene.add(dir);

      // Simple ground grid
      const grid = new THREE.GridHelper(400, 20, 0x334455, 0x223344);
      grid.position.y = -1;
      scene.add(grid);

      // Add extruded features if provided
      if (features && features.length > 0) {
        features.slice(0, 20).forEach((f, i) => {
          const geom = f.geometry;
          if (!geom) return;

          // Very rough extrusion for demo (polygons become boxes)
          if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
            const height = (8 + (i % 5) * 3) * heightExaggeration;
            const box = new THREE.BoxGeometry(18, height, 12);
            const mat = new THREE.MeshLambertMaterial({ color: 0x4ade80 });
            const mesh = new THREE.Mesh(box, mat);
            // Place roughly in center of map area
            mesh.position.set(-80 + (i % 5) * 35, height / 2, -60 + Math.floor(i / 5) * 30);
            scene.add(mesh);
          }
        });
      } else {
        // Demo terrain-like surface
        const planeGeo = new THREE.PlaneGeometry(220, 180, 24, 18);
        const verts = planeGeo.attributes.position;
        for (let i = 0; i < verts.count; i++) {
          const z = Math.sin(i * 0.7) * 6 + Math.cos(i * 1.1) * 4;
          verts.setZ(i, z * heightExaggeration);
        }
        planeGeo.computeVertexNormals();
        const terrain = new THREE.Mesh(
          planeGeo,
          new THREE.MeshLambertMaterial({ color: 0x64748b, flatShading: false, wireframe: false })
        );
        terrain.rotation.x = -Math.PI / 2.1;
        scene.add(terrain);
      }

      // Simple orbit with mouse
      let isDown = false;
      let prevX = 0,
        prevY = 0;
      const onDown = (e: MouseEvent) => {
        isDown = true;
        prevX = e.clientX;
        prevY = e.clientY;
      };
      const onUp = () => {
        isDown = false;
      };
      const onMove = (e: MouseEvent) => {
        if (!isDown) return;
        const dx = e.clientX - prevX;
        const dy = e.clientY - prevY;
        camera.position.x -= dx * 0.6;
        camera.position.y += dy * 0.6;
        prevX = e.clientX;
        prevY = e.clientY;
      };
      renderer.domElement.addEventListener('mousedown', onDown);
      window.addEventListener('mouseup', onUp);
      renderer.domElement.addEventListener('mousemove', onMove);

      const animate = () => {
        requestAnimationFrame(animate);
        camera.lookAt(0, 20, 0);
        renderer.render(scene, camera);
      };
      animate();

      sceneRef.current = { scene, camera, renderer };

      cleanup = () => {
        renderer.domElement.removeEventListener('mousedown', onDown);
        window.removeEventListener('mouseup', onUp);
        renderer.domElement.removeEventListener('mousemove', onMove);
        if (renderer.domElement.parentNode)
          renderer.domElement.parentNode.removeChild(renderer.domElement);
        renderer.dispose();
      };
    };

    loadThree().catch(() => {
      // Fallback nice message
      if (containerRef.current) {
        containerRef.current.innerHTML = `
          <div style="padding:16px; color:#64748b; font-size:12px; text-align:center;">
            3D Scene ready.<br/>
            (Three.js CDN load failed — in production this uses bundled three.js + React Three Fiber for full digital twin experience.)
          </div>`;
      }
    });

    return () => cleanup();
  }, [features, heightExaggeration]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: 280,
        bgcolor: '#0f172a',
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid #334155',
      }}
    />
  );
};

export default ThreeScene;
